import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Select, Button, Spin } from "antd";
import {
  CloseOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import styles from "./kpiView.module.scss";
import {
  filterSelectOption,
  mergeMetricListLabels,
  parseKpiDashboardIds,
  parseKpiMetrics,
  resolveKpiPivots,
  sortSourcesByLastUpdate,
} from "./kpiViewUtils";

function normalizeDashboardPivots(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof raw === "object" ? raw : null;
}

function collectPivotValuesFromBucket(bucket) {
  const values = new Set();
  if (Array.isArray(bucket)) {
    bucket.forEach((val) => {
      if (val != null && String(val).trim()) values.add(String(val));
    });
    return values;
  }
  if (bucket && typeof bucket === "object") {
    Object.entries(bucket).forEach(([val, cnt]) => {
      if (val == null || !String(val).trim()) return;
      if (cnt == null || Number(cnt) > 0) values.add(String(val));
    });
  }
  return values;
}

function extractPivotKeysFromResponse(successPayload) {
  if (!Array.isArray(successPayload)) return [];
  const keys = new Set();
  successPayload.forEach((item) => {
    Object.entries(item || {}).forEach(([, rawPivots]) => {
      const dashboardPivots = normalizeDashboardPivots(rawPivots);
      Object.keys(dashboardPivots || {}).forEach((key) => keys.add(key));
    });
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

function extractPivotValuesFromResponse(successPayload, pivotKey) {
  if (!Array.isArray(successPayload) || !pivotKey) return [];
  const values = new Set();
  successPayload.forEach((item) => {
    Object.entries(item || {}).forEach(([, rawPivots]) => {
      const dashboardPivots = normalizeDashboardPivots(rawPivots);
      collectPivotValuesFromBucket(dashboardPivots?.[pivotKey]).forEach((val) =>
        values.add(val)
      );
    });
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

export default function EditKpiModal({
  open,
  item,
  sources = [],
  onClose,
  onSubmit,
  loading = false,
}) {
  const { t } = useTranslation();
  const [dashboardIds, setDashboardIds] = useState([]);
  const [metric, setMetric] = useState(null);
  const [metricOptions, setMetricOptions] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [pivots, setPivots] = useState([]);
  const [pivotData, setPivotData] = useState([]);
  const [pivotKeys, setPivotKeys] = useState([]);
  const [pivotsLoading, setPivotsLoading] = useState(false);
  const [pivotDraft, setPivotDraft] = useState({ key: "", value: "" });
  const [pivotValuePickerOpen, setPivotValuePickerOpen] = useState(false);
  const [pivotValueQuery, setPivotValueQuery] = useState("");
  const pivotValuePickerRef = useRef(null);
  const pivotValueSearchRef = useRef(null);

  const sortedSources = useMemo(
    () => sortSourcesByLastUpdate(sources),
    [sources]
  );

  const dashboardOptions = useMemo(
    () =>
      sortedSources.map((s) => ({
        value: s.id,
        label: s.Name,
      })),
    [sortedSources]
  );

  const metricSelectOptions = useMemo(
    () =>
      metricOptions.map((label) => ({
        value: label,
        label,
      })),
    [metricOptions]
  );

  useEffect(() => {
    if (!open || !item) return;
    const ids = parseKpiDashboardIds(item);
    setDashboardIds(ids.length ? ids : sortedSources.length ? [sortedSources[0].id] : []);
    const metrics = parseKpiMetrics(item);
    setMetric(metrics[0] || null);
    setPivots(resolveKpiPivots(item));
    setPivotDraft({ key: "", value: "" });
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
  }, [open, item, sortedSources]);

  useEffect(() => {
    if (!open || dashboardIds.length === 0) {
      setMetricOptions([]);
      return undefined;
    }

    let cancelled = false;
    const loadMetrics = async () => {
      setMetricsLoading(true);
      const result = await apiRequest(
        "executive_summary/v2/metricList",
        "POST",
        {
          dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)),
          block_location: "Top",
        },
        true
      );
      if (cancelled) return;
      const labels = mergeMetricListLabels(result?.success);
      setMetricOptions(labels);
      setMetric((current) =>
        current && !labels.includes(current) ? null : current
      );
      setMetricsLoading(false);
    };

    loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [open, dashboardIds]);

  useEffect(() => {
    if (!open || dashboardIds.length === 0) {
      setPivotData([]);
      setPivotKeys([]);
      setPivotsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadPivots = async () => {
      setPivotsLoading(true);
      const result = await apiRequest(
        "report/pivots",
        "POST",
        { dashboard_ids: dashboardIds.map((id) => parseInt(id, 10)) },
        true
      );
      if (cancelled) return;
      if (result?.success) {
        const data = result.success;
        setPivotData(data);
        const keys = extractPivotKeysFromResponse(data);
        setPivotKeys(keys);
        setPivotDraft((prev) => ({
          key: prev.key && keys.includes(prev.key) ? prev.key : keys[0] || "",
          value: "",
        }));
        setPivots((prev) => prev.filter((p) => keys.includes(p.key)));
      } else {
        setPivotData([]);
        setPivotKeys([]);
      }
      setPivotsLoading(false);
    };

    loadPivots();
    return () => {
      cancelled = true;
    };
  }, [open, dashboardIds]);

  const pivotValueOptions = useMemo(
    () => extractPivotValuesFromResponse(pivotData, pivotDraft.key),
    [pivotData, pivotDraft.key]
  );

  const filteredPivotValueOptions = useMemo(() => {
    const q = pivotValueQuery.trim().toLowerCase();
    if (!q) return pivotValueOptions;
    return pivotValueOptions.filter((val) =>
      String(val).toLowerCase().includes(q)
    );
  }, [pivotValueOptions, pivotValueQuery]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        pivotValuePickerRef.current &&
        !pivotValuePickerRef.current.contains(e.target)
      ) {
        setPivotValuePickerOpen(false);
        setPivotValueQuery("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (pivotValuePickerOpen) {
      const timer = window.setTimeout(() => {
        if (pivotValueSearchRef.current?.focus) {
          pivotValueSearchRef.current.focus();
        }
      }, 0);
      return () => window.clearTimeout(timer);
    }
    setPivotValueQuery("");
    return undefined;
  }, [pivotValuePickerOpen]);

  const appendPivotChip = (prev, key, value) => {
    const nextKey = String(key ?? "").trim();
    const nextValue = String(value ?? "").trim();
    if (!nextKey || !nextValue) return prev;
    if (prev.some((p) => p.key === nextKey && String(p.value) === nextValue)) {
      return prev;
    }
    return [...prev, { key: nextKey, value: nextValue }];
  };

  const selectPivotValue = (value) => {
    const key = String(pivotDraft.key ?? "").trim();
    const nextValue = String(value ?? "").trim();
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
    if (key && nextValue) {
      setPivots((prev) => appendPivotChip(prev, key, nextValue));
      setPivotDraft({ key, value: "" });
    }
  };

  const addPivot = () => {
    const key = String(pivotDraft.key ?? "").trim();
    const value = String(pivotDraft.value ?? "").trim();
    if (!key || !value) return;
    setPivots((prev) => appendPivotChip(prev, key, value));
    setPivotDraft({ key, value: "" });
    setPivotValuePickerOpen(false);
    setPivotValueQuery("");
  };

  const removePivot = (index) => {
    setPivots((prev) => prev.filter((_, i) => i !== index));
  };

  const pivotsForSubmit = useMemo(() => {
    const list = [...pivots];
    const draftKey = String(pivotDraft.key ?? "").trim();
    const draftValue = String(pivotDraft.value ?? "").trim();
    if (draftKey && draftValue) {
      const exists = list.some(
        (p) => p.key === draftKey && String(p.value) === draftValue
      );
      if (!exists) list.push({ key: draftKey, value: draftValue });
    }
    return list;
  }, [pivots, pivotDraft.key, pivotDraft.value]);

  const canSave =
    dashboardIds.length > 0 && (Boolean(metric) || pivotsForSubmit.length > 0);

  const handleSubmit = async () => {
    if (!onSubmit || !canSave) return;
    const ok = await onSubmit({
      item,
      metric,
      dashboardIds,
      pivots: pivotsForSubmit,
    });
    if (ok && onClose) onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t("team_kpi_view.26")}
      destroyOnClose
      width={480}
      className={styles.editKpiModal}
    >
      <div className={styles.editKpiForm}>
        <div className={styles.editKpiField}>
          <label className={styles.editKpiLabel}>{t("team_kpi_view.5")}</label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={t("team_kpi_view.6")}
            value={dashboardIds}
            onChange={setDashboardIds}
            showSearch
            optionFilterProp="label"
            filterOption={filterSelectOption}
            options={dashboardOptions}
          />
        </div>

        <div className={styles.editKpiField}>
          <label className={styles.editKpiLabel}>{t("team_kpi_view.13")}</label>
          <Select
            style={{ width: "100%" }}
            placeholder={t("team_kpi_view.12")}
            value={metric}
            onChange={setMetric}
            showSearch
            loading={metricsLoading}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            options={metricSelectOptions}
            allowClear
          />
        </div>

        <div className={styles.editKpiField}>
          <label className={styles.editKpiLabel}>{t("team_kpi_view.42")}</label>
          <Spin spinning={pivotsLoading}>
            <div className={styles.pivotInlineForm}>
              <label className={styles.pivotInlineField}>
                <span>{t("team_kpi_view.builder_pivot_key", "Pivot key")}</span>
                <select
                  value={pivotDraft.key}
                  onChange={(e) =>
                    setPivotDraft({ key: e.target.value, value: "" })
                  }
                  disabled={pivotKeys.length === 0}
                >
                  {pivotKeys.length === 0 ? (
                    <option value="">
                      {t("team_kpi_view.builder_pivot_none", "No pivots")}
                    </option>
                  ) : (
                    pivotKeys.map((key) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <div className={styles.pivotInlineField}>
                <span>{t("team_kpi_view.builder_pivot_value", "Pivot value")}</span>
                <div
                  className={styles.pivotValuePicker}
                  ref={pivotValuePickerRef}
                >
                  <button
                    type="button"
                    className={styles.pivotValueTrigger}
                    onClick={() => {
                      if (pivotsLoading || pivotValueOptions.length === 0) return;
                      setPivotValuePickerOpen((open) => !open);
                    }}
                    disabled={pivotsLoading || pivotValueOptions.length === 0}
                  >
                    <span className={styles.pivotValueTriggerLabel}>
                      {pivotDraft.value ||
                        (pivotValueOptions.length > 0
                          ? t("team_kpi_view.builder_pivot_select", {
                              count: pivotValueOptions.length,
                              defaultValue: "Select ({{count}})",
                            })
                          : t("team_kpi_view.builder_pivot_no_value", "No values"))}
                    </span>
                  </button>
                  {pivotValuePickerOpen && (
                    <div className={styles.pivotValueDropdown}>
                      <div className={styles.builderDropdownSearch}>
                        <SearchOutlined
                          className={styles.builderDropdownSearchIcon}
                        />
                        <input
                          ref={pivotValueSearchRef}
                          type="search"
                          className={styles.builderDropdownSearchInput}
                          placeholder={t(
                            "team_kpi_view.builder_search_pivot_value",
                            "Search pivot values...",
                          )}
                          value={pivotValueQuery}
                          onChange={(e) => setPivotValueQuery(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className={styles.builderDropdownList}>
                        {filteredPivotValueOptions.length > 0 ? (
                          filteredPivotValueOptions.map((val) => (
                            <button
                              key={val}
                              type="button"
                              className={styles.builderDropdownItem}
                              onClick={() => selectPivotValue(val)}
                            >
                              {val}
                            </button>
                          ))
                        ) : (
                          <div className={styles.builderDropdownEmpty}>
                            {t("team_kpi_view.builder_no_results", "No results")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className={styles.pivotInlineSubmit}
                onClick={addPivot}
                disabled={!pivotDraft.key || !pivotDraft.value}
              >
                {t("team_kpi_view.builder_add", "Add")}
              </button>
            </div>
          </Spin>
          <div className={styles.chipRow}>
            {pivots.map((pivot, index) => (
              <span
                key={`${pivot.key}-${pivot.value}-${index}`}
                className={styles.pivotChip}
              >
                <FilterOutlined className={styles.chipIcon} />
                <span className={styles.chipLabel}>
                  {pivot.key} = {pivot.value}
                </span>
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removePivot(index)}
                  aria-label={t("team_kpi_view.remove", "Remove")}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.editKpiActions}>
          <Button onClick={onClose}>{t("button.cancel")}</Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!canSave}
            onClick={handleSubmit}
          >
            {t("team_kpi_view.30")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
